﻿import { create } from 'zustand';
import type { VisualComponent, Page } from '@dtwin/shared';
import { generateId } from '@dtwin/shared';
import { fetchPage, updatePage, createPage, updatePageComponents } from '../api/pages';

interface DashboardState {
  currentPage: Page | null;
  components: VisualComponent[];
  selectedComponentId: string | null;
  /** 2026-09-01｜多选组件（Ctrl/框选） */
  selectedComponentIds: string[];
  saving: boolean;
  dirty: boolean;
  /** 椤甸潰鏄惁宸插湪鏈嶅姟绔寔涔呭寲锛坙oadPage 鎴愬姛 = true锛涙湰鍦板洖閫€ = false锛?*/
  persisted: boolean;
  /** 鏈€杩戜竴娆′繚瀛橀敊璇俊鎭紙鎴愬姛涓?null锛?*/
  saveError: string | null;

  setPage: (page: Page) => void;
  loadPage: (id: string) => Promise<void>;
  /** 骞傜瓑淇濆瓨锛氳繑鍥炴槸鍚︽垚鍔?*/
  savePage: () => Promise<boolean>;
  /** 鍙洿鏂?currentPage.config锛屼笉纰扮粍浠?*/
  patchPageConfig: (config: Record<string, unknown>) => void;
  /** 只更新 currentPage 根字段（如 sceneId/name），不碰组件；置 dirty（Q3：大屏项目级「关联3D场景」） */
  patchPage: (partial: Partial<Page>) => void;
  addComponent: (component: Omit<VisualComponent, 'id' | 'pageId'>) => void;
  updateComponent: (id: string, updates: Partial<VisualComponent>) => void;
  removeComponent: (id: string) => void;
  selectComponent: (id: string | null) => void;
  selectMultiple: (id: string) => void;
  alignSelected: (align: 'left' | 'right' | 'top' | 'bottom' | 'hCenter' | 'vCenter') => void;
  reorderComponent: (id: string, position: { x: number; y: number }) => void;
  resizeComponent: (id: string, size: { width: number; height: number }) => void;
  moveComponentZ: (id: string, dir: 'up' | 'down' | 'top' | 'bottom') => void;
  /** 澶嶅埗缁勪欢锛堟柊澧炲埌鍋忕Щ浣嶇疆骞堕€変腑锛?*/
  duplicateComponent: (id: string) => void;
  /** 閿佸畾/瑙ｉ攣缁勪欢 */
  setComponentLocked: (id: string, locked: boolean) => void;
  /** 鍥惧眰鎷栨嫿鎺掑簭锛歰rderedIds 涓鸿瑙変粠涓婂埌涓嬬殑椤哄簭锛屾寜搴忛噸璁?zIndex */
  reorderLayers: (orderedIds: string[]) => void;
  setPersisted: (persisted: boolean) => void;
  /** 缁勪欢鍒锋柊淇″彿锛堥樁娈?锝滆仈鍔?refresh 鍔ㄤ綔锛夛細compId 鈫?鑷璁℃暟锛寀seWidgetData 璁㈤槄骞堕噸鎷?*/
  refreshSignals: Record<string, number>;
  signalRefresh: (id: string) => void;
}

function zOf(c: VisualComponent): number {
  const z = c.config?.zIndex ?? c.style?.zIndex ?? c.base?.zIndex;
  return typeof z === 'number' ? z : 1;
}

function makeLocalPage(id: string): Page {  return {
    id,
    name: '鏈湴澶у睆',
    config: { width: 1920, height: 1080, backgroundColor: '#0f0f2e' },
    components: [],
    userId: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  } as any;
}

const useDashboardStore = create<DashboardState>((set, get) => ({
  currentPage: null,
  components: [],
  selectedComponentId: null,
  selectedComponentIds: [],
  saving: false,
  dirty: false,
  persisted: false,
  saveError: null,
  refreshSignals: {},

  setPage: (page) => set({ currentPage: page, components: page.components, dirty: false, persisted: true, saveError: null }),

  patchPageConfig: (config) =>
    set((state) => {
      if (!state.currentPage) return {};
      return { currentPage: { ...state.currentPage, config: config as unknown as Page['config'] } };
    }),

  patchPage: (partial) =>
    set((state) => {
      if (!state.currentPage) return {};
      return { currentPage: { ...state.currentPage, ...partial }, dirty: true };
    }),

  loadPage: async (id) => {
    // 2026-09-14 修复（FE-31）：本地已加载同页且存在未保存修改（dirty）→ 保留本地编辑，
    // 不用服务端旧版本覆盖（原行为：恢复网络后重新进入大屏，先前配置的 dataSource/URL 被旧快照打回）
    const st0 = get();
    if (st0.dirty && st0.currentPage && st0.currentPage.id === id && st0.components.length > 0) {
      set({ saveError: '存在未保存的本地修改，已保留本地编辑（请点击保存以同步到服务端）' });
      return;
    }
    try {
      const page = await fetchPage(id);
      set({ currentPage: page, components: page.components, dirty: false, persisted: true, saveError: null });
    } catch (e: any) {
      // 2026-08-26锝滈槻瑕嗙洊淇锛氬姞杞藉け璐ユ椂鑻?store 宸叉寔鏈夎椤垫暟鎹紙閲嶈繘缂栬緫鍣級锛屼繚鐣欐湰鍦扮紪杈戜笉娓呯┖锛?      // 鍘熼€昏緫鐩存帴 makeLocalPage+绌虹粍浠讹紝鐢ㄦ埛鐐逛繚瀛樹細鎶婁簯绔ぇ灞忕粍浠跺叏閲忚鐩栨垚绌猴紙浜戠 4cf9569d 缁勪欢涓㈠け鏍瑰洜锛?      console.warn('Failed to load page from server, using local fallback', e?.message);
      const st = get();
      if (st.currentPage && st.currentPage.id === id && st.components.length > 0) {
        set({ saveError: '椤甸潰鍔犺浇澶辫触锛屽凡淇濈暀鏈湴缂栬緫锛堜繚瀛樺墠璇风‘璁ょ綉缁滐級' });
      } else {
        set({ currentPage: makeLocalPage(id), components: [], dirty: false, persisted: false, saveError: null });
      }
    }
  },

  savePage: async () => {
    const { currentPage, components } = get();
    if (!currentPage) return false;
    set({ saving: true, saveError: null });
    let targetId = currentPage.id;
    try {
      // 1) 淇濆瓨椤甸潰鍩虹淇℃伅锛涗粎褰撴湇鍔＄鏄庣‘ 404锛堥〉闈笉瀛樺湪锛夋椂鍥為€€涓哄垱寤猴紝涓斾繚鐣欏師 id 閬垮厤 URL 澶辨晥
      if (!get().persisted) {
        try {
          await updatePage(targetId, {
            name: currentPage.name,
            config: currentPage.config,
            description: currentPage.description, thumbnail: (currentPage as any).thumbnail,
            sceneId: currentPage.sceneId,
          });
        } catch (e: any) {
          if (e?.response?.status !== 404) throw e;
          const created = await createPage({
            id: targetId,
            name: currentPage.name,
            description: currentPage.description, thumbnail: (currentPage as any).thumbnail,
            sceneId: currentPage.sceneId,
            config: currentPage.config,
          });
          if (created?.id) targetId = created.id;
        }
      } else {
        await updatePage(targetId, {
          name: currentPage.name,
          config: currentPage.config,
          description: currentPage.description, thumbnail: (currentPage as any).thumbnail,
          sceneId: currentPage.sceneId,
        });
      }

      // 2) 淇濊瘉缁勪欢 pageId 涓庣洰鏍?id 涓€鑷村悗鎵归噺淇濆瓨
      const comps = components.map((c) => (c.pageId === targetId ? c : { ...c, pageId: targetId }));
      await updatePageComponents(targetId, comps);

      set((state) => ({
        // 2026-10-11：同步回写页面快照的 components，避免「保存后 currentPage.components 仍是旧数据」
        // 导致只读叠加/预览/LayerPanel 计数读到陈旧列表（新加组件不显示，重开项目才出现）
        currentPage: { ...(state.currentPage as Page), id: targetId, components: comps },
        components: comps,
        persisted: true,
        dirty: false,
        saveError: null,
      }));
      return true;
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || '淇濆瓨澶辫触';
      console.warn('Save failed, changes kept locally', msg);
      set({ saveError: String(msg) });
      return false;
    } finally {
      set({ saving: false });
    }
  },

  addComponent: (comp) => {
    // 2026-09-01｜新组件默认最上层（zIndex = 当前最大 + 1）
    const maxZ = get().components.reduce(
      (m, c) => Math.max(m, Number(c.config?.zIndex) || 0, Number(c.style?.zIndex) || 0, Number(c.base?.zIndex) || 0),
      0,
    );
    const newComp: VisualComponent = {
      ...comp,
      id: generateId(),
      pageId: get().currentPage?.id ?? '',
      config: { ...(comp.config || {}), zIndex: maxZ + 1 },
      base: { ...(comp.base || {}), zIndex: maxZ + 1 },
    };
    set((state) => ({ components: [...state.components, newComp], dirty: true }));
  },

  updateComponent: (id, updates) =>
    set((state) => ({
      components: state.components.map((c) => (c.id === id ? { ...c, ...updates } : c)),
      dirty: true,
    })),

  removeComponent: (id) =>
    set((state) => ({
      components: state.components.filter((c) => c.id !== id),
      selectedComponentId: state.selectedComponentId === id ? null : state.selectedComponentId,
      dirty: true,
    })),

  selectComponent: (id) => set({ selectedComponentId: id, selectedComponentIds: id ? [id] : [] }),

  // 2026-09-01｜多选：Ctrl/框选时 toggle（保留/移除），否则单选
  selectMultiple: (id) =>
    set((state) => {
      const has = state.selectedComponentIds.includes(id);
      const ids = has ? state.selectedComponentIds.filter((x) => x !== id) : [...state.selectedComponentIds, id];
      return { selectedComponentIds: ids, selectedComponentId: ids.length > 0 ? ids[ids.length - 1] : null };
    }),

  // 2026-09-01｜多选对齐：left/right/top/bottom/hCenter/vCenter（组件间对齐，以选中组件包围盒为基准）
  alignSelected: (align) =>
    set((state) => {
      const ids = state.selectedComponentIds;
      const sel = state.components.filter((c) => ids.includes(c.id));
      if (sel.length < 2) return state;
      const minX = Math.min(...sel.map((c) => c.position?.x ?? 0));
      const maxX = Math.max(...sel.map((c) => (c.position?.x ?? 0) + (c.size?.width ?? 0)));
      const minY = Math.min(...sel.map((c) => c.position?.y ?? 0));
      const maxY = Math.max(...sel.map((c) => (c.position?.y ?? 0) + (c.size?.height ?? 0)));
      const components = state.components.map((c) => {
        if (!ids.includes(c.id)) return c;
        const x = c.position?.x ?? 0;
        const y = c.position?.y ?? 0;
        const w = c.size?.width ?? 0;
        const h = c.size?.height ?? 0;
        let nx = x, ny = y;
        if (align === 'left') nx = minX;
        else if (align === 'right') nx = maxX - w;
        else if (align === 'hCenter') nx = minX + (maxX - minX) / 2 - w / 2;
        else if (align === 'top') ny = minY;
        else if (align === 'bottom') ny = maxY - h;
        else if (align === 'vCenter') ny = minY + (maxY - minY) / 2 - h / 2;
        return { ...c, position: { x: nx, y: ny } };
      });
      return { components, dirty: true };
    }),

  setPersisted: (persisted) => set({ persisted }),

  reorderComponent: (id, position) =>
    set((state) => ({
      components: state.components.map((c) => (c.id === id ? { ...c, position } : c)),
      dirty: true,
    })),

  resizeComponent: (id, size) =>
    set((state) => ({
      components: state.components.map((c) =>
        c.id === id ? { ...c, size } : c,
      ),
      dirty: true,
    })),

  moveComponentZ: (id, dir) =>
    set((state) => {
      const list = [...state.components].sort((a, b) => zOf(a) - zOf(b));
      const idx = list.findIndex((c) => c.id === id);
      if (idx === -1) return state;
      const target = list[idx];
      list.splice(idx, 1);
      if (dir === 'up') list.splice(Math.min(idx + 1, list.length), 0, target);
      else if (dir === 'down') list.splice(Math.max(idx - 1, 0), 0, target);
      else if (dir === 'top') list.push(target);
      else if (dir === 'bottom') list.unshift(target);
      const components = list.map((c, i) => ({ ...c, config: { ...(c.config || {}), zIndex: i + 1 } }));
      return { components, dirty: true };
    }),

  duplicateComponent: (id) =>
    set((state) => {
      const src = state.components.find((c) => c.id === id);
      if (!src) return state;
      const copy: VisualComponent = {
        ...src,
        id: generateId(),
        pageId: state.currentPage?.id ?? src.pageId,
        position: { x: (src.position?.x ?? 0) + 20, y: (src.position?.y ?? 0) + 20 },
      };
      return { components: [...state.components, copy], selectedComponentId: copy.id, dirty: true };
    }),

  setComponentLocked: (id, locked) =>
    set((state) => ({
      components: state.components.map((c) =>
        c.id === id ? { ...c, base: { ...(c.base || {}), locked }, config: { ...(c.config || {}), locked } } : c
      ),
      dirty: true,
    })),

  reorderLayers: (orderedIds) =>
    set((state) => {
      const byId = new Map(state.components.map((c) => [c.id, c]));
      const ordered = orderedIds.map((id) => byId.get(id)).filter(Boolean) as VisualComponent[];
      const others = state.components.filter((c) => !orderedIds.includes(c.id));
      // 2026-10-13 修复（用户反馈"图层顺序无法直接拖动"）：orderedIds 为视觉"从上到下"（zIndex 大→小），
      // 顶层必须取最高 zIndex——此前按数组 index+1 赋值导致顺序整体反转（松手后又弹回/颠倒）
      const all = [...ordered, ...others];
      const total = all.length;
      const components = all.map((c, i) => ({ ...c, config: { ...(c.config || {}), zIndex: total - i } }));
      return { components, dirty: true };
    }),

  signalRefresh: (id) =>
    set((state) => ({
      refreshSignals: { ...state.refreshSignals, [id]: (state.refreshSignals[id] || 0) + 1 },
    })),
}));

export default useDashboardStore;
